import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import { format, isSameYear } from 'date-fns';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { DialogVariantOptions } from 'shared/assets/styles';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';
import { useToday } from 'shared/contexts/today';
import { DateFormatOptions } from 'shared/types/date-format-options';
import { ScheduleChangeMode } from 'shared/types/habit-schedule';

import * as Styled from './habit-schedule-confirm-changes-dialog.style';

export type HabitScheduleConfirmChangesDialogProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: (mode: ScheduleChangeMode) => void;
  startDate: Date;
  dateFormat?: DateFormatOptions;
};

export const HabitScheduleConfirmChangesDialog: React.FC<
  HabitScheduleConfirmChangesDialogProps
> = ({ open, onClose, onConfirm, startDate }) => {
  const { t } = useTranslation();
  const today = useToday();
  const dateFormatted = isSameYear(startDate, today)
    ? format(startDate, 'MMM d')
    : format(startDate, "MMM d ''yy");

  const handleConfirmOriginal = () => onConfirm(ScheduleChangeMode.Original);
  const handleConfirmToday = () => onConfirm(ScheduleChangeMode.Today);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      variant={DialogVariantOptions.ExtraSmall}
    >
      <Styled.Container>
        <Styled.Header>
          <Typography variant="h5" component="h1">
            {t('dialogs.habit-schedule.confirm-changes.title')}
          </Typography>
        </Styled.Header>

        <Styled.Body>
          <Typography variant="body2">
            <Trans
              i18nKey="dialogs.habit-schedule.confirm-changes.body"
              values={{ date: dateFormatted }}
              components={{ bold: <strong /> }}
            />
          </Typography>
        </Styled.Body>

        <Styled.Footer>
          <Button
            variant={Variants.Contained}
            size={Sizes.Medium}
            color={Colors.Primary}
            onClick={handleConfirmToday}
            fullWidth
            alignCenter
          >
            {t(
              'dialogs.habit-schedule.confirm-changes.buttons.start-today.label',
            )}
          </Button>

          <Button
            variant={Variants.Contained}
            size={Sizes.Medium}
            color={Colors.Primary}
            onClick={handleConfirmOriginal}
            fullWidth
            alignCenter
          >
            {t(
              'dialogs.habit-schedule.confirm-changes.buttons.apply-since.label',
              {
                date: dateFormatted,
              },
            )}
          </Button>

          <Button
            variant={Variants.Contained}
            size={Sizes.Medium}
            color={Colors.White}
            onClick={onClose}
            fullWidth
            alignCenter
          >
            {t(
              'dialogs.habit-schedule.confirm-changes.buttons.select-start-date.label',
            )}
          </Button>
        </Styled.Footer>
      </Styled.Container>
    </Dialog>
  );
};
